import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { Download, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/keeper/batches`;
const link = "inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#B5A642] hover:text-[#E8DDC7] transition-colors";

const save = (href, name) => {
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  a.click();
};

const BatchQR = ({ b, origin }) => {
  const wrap = useRef(null);
  const url = `${origin}/batch/${encodeURIComponent(b.batch_no)}`;
  const png = () => save(wrap.current.querySelector("canvas").toDataURL("image/png"), `batch-${b.batch_no}-qr.png`);
  const svg = () => {
    const xml = new XMLSerializer().serializeToString(wrap.current.querySelector("svg"));
    const blob = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml" }));
    save(blob, `batch-${b.batch_no}-qr.svg`);
    setTimeout(() => URL.revokeObjectURL(blob), 1000);
  };
  return (
    <li className="border border-[#303B2D]/70 rounded-sm p-5 flex flex-col sm:flex-row gap-5" data-testid={`batch-qr-${b.batch_no}`}>
      <div ref={wrap} className="bg-[#E8DDC7] p-3 rounded-sm self-start">
        <QRCodeCanvas value={url} size={512} level="M" marginSize={2} bgColor="#E8DDC7" fgColor="#1C1F1E" style={{ width: 128, height: 128 }} />
        <QRCodeSVG value={url} size={512} level="M" marginSize={2} bgColor="#FFFFFF" fgColor="#000000" className="hidden" />
      </div>
      <div className="min-w-0">
        <p className="font-display text-2xl text-[#E8DDC7]">{b.batch_no}</p>
        <p className="text-[13px] text-[#D3CEC4]/70">{b.title}{b.sample && " · sample"}</p>
        <p className="mt-1 font-ledger text-[12px] text-[#D3CEC4]/55 break-all" data-testid={`batch-url-${b.batch_no}`}>{url}</p>
        <p className="mt-1 text-[12px] text-[#D3CEC4]/55">{b.real_certificate ? "Real lab certificate on file" : "Sample certificate"}</p>
        <div className="mt-3 flex flex-wrap gap-4">
          <button className={link} onClick={png} data-testid={`batch-qr-png-${b.batch_no}`}><Download className="w-3.5 h-3.5" /> PNG</button>
          <button className={link} onClick={svg} data-testid={`batch-qr-svg-${b.batch_no}`}><Download className="w-3.5 h-3.5" /> SVG for print</button>
          <a className={link} href={url} target="_blank" rel="noreferrer" data-testid={`batch-open-${b.batch_no}`}><ExternalLink className="w-3.5 h-3.5" /> Open page</a>
        </div>
      </div>
    </li>
  );
};

export const KeeperBatches = ({ token }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(API, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
  }, [token]);
  const origin = window.location.origin;
  if (!rows) return <p className="mt-8 text-[#D3CEC4]/50">Opening the ledger…</p>;
  return (
    <div className="mt-6" data-testid="keeper-batches">
      <p className="text-[14px] text-[#D3CEC4]/70">A QR code for every batch. Print it on the label and customers land on that batch's own page with its lab certificate.</p>
      <p className="mt-1 text-[12px] text-[#C9A96B]" data-testid="batches-origin-note">These codes point to {origin}. Download them from your live site address so printed labels never point to a preview.</p>
      {rows.length === 0 ? <p className="mt-8 font-display italic text-[#D3CEC4]/55" data-testid="batches-empty">No batches in the ledger yet.</p> : (
        <ul className="mt-6 grid lg:grid-cols-2 gap-4">{rows.map((b) => <BatchQR key={b.batch_no} b={b} origin={origin} />)}</ul>
      )}
    </div>
  );
};
